/**
 * Evita la ejecución por un periodo de tiempo.
 *
 * Useful dealing with scroll events.
 *
 * @param {Function} callback
 * @return {Function} the debounced callback
 */
export const debounce = callback => {
  const timeout = 300
  let busy = false

  return args => {
    if (busy) return null
    busy = true
    setTimeout(() => { busy = false }, timeout)

    return callback(args)
  }
}

export const flat = arr => arr.reduce((sum, item) =>
  Array.isArray(item)
    ? sum.concat(flat(item))
    : sum.concat(item), [])

/**
 * Similar to recent Array.prototype.flatMap.
 */
export const flatMap = function (fn) {
  return flat(this.map(fn))
}

export const trunkFloat = (num, decimals = 5) =>
  Math.floor(num * (10 ** decimals)) / (10 ** decimals)

/**
 * Devuelve un nuevo array ordenado según una función.
 *
 * @param {Function} mapper para buscar algo que pueda ser ordenado.
 * @param {Array} items
 *
 * @return {Array} ordenado.
 */
export const mapSort = mapper => items => {
  if (items.length === 0) return []

  const [head, ...tail] = items

  return [
    ...mapSort(mapper)(tail.filter(i => mapper(i) <= mapper(head))),
    head,
    ...mapSort(mapper)(tail.filter(i => mapper(i) > mapper(head)))
  ]
}

/**
 * Cleans an input field, accepting only numbers and a dot '.'
 *
 * @param {Object} @input event e containing e.target.value
 */
export const cleanAmountInput = e => {
  e.target.value = e.target.value.toString().replace(/[^\d.]/g, '')
}

/**
 * Cleans an input field, accepting only numbers (integer)
 *
 * @param {Object} @input event e containing e.target.value
 */
export const cleanIntegerInput = e => {
  e.target.value = e.target.value.toString().replace(/[^\d]/g, '')
}

export const validPriceAmount = value => {
  const untilTwoDec = /^\d+(\.\d{1,2})?$/

  return (value !== null && value > 0 && !!value.toString().match(untilTwoDec)) || value == null || value == ''
}

export const validEmail = email => {
  const re = /\S+@\S+\.\S+/

  return !!email.match(re)
}

export const isValidUrl = (web) => {
  let pattern = /[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b/g

  return pattern.test(web)
}

/**
 * Just for clone an object using json.
 */
export const clone = originalObject => JSON.parse(JSON.stringify(originalObject))

/**
 * Functional reverse array.
 */
export const reverseArray = items => items.map((_, i, ls) => ls[ls.length - 1 - i])

export const sellFormula = ({images, required, optionals, totalRequired, totalOptionals}) => {
  const maxImages = 5
  const imagesPenalty = 0.5
  const imagesMaxWeight = 50
  const requiredMaxWeight = 50
  // const optionalsMaxWeight = 30

  const fromImages = imagesMaxWeight / maxImages * Math.min(images, maxImages)
  const fromRequired = requiredMaxWeight / totalRequired * required
  // const fromOptionals = optionalsMaxWeight / totalOptionals * optionals

  return Math.floor((fromImages + fromRequired) * (images === 0 ? imagesPenalty : 1))
}

export const hlFormula = (items) => {
  const factors = {
    'GLD': 7,
    'SLVR': 4,
    '3DAYS': 1,
    '7DAYS': 2,
    '15DAYS': 4,
    'BND': 1,
    'ROT': 1,
    'HOME': 7
  }

  return items.reduce((acc, item) => (factors[item] || 0) + acc, 0)
}

export const passwordChecker = {
  checkInvalidLength: str => !/.{6,}/.test(str),
  checkInvalidNumbers: str => !/\d/.test(str),
  checkInvalidUpperCase: str => !/[A-Z]/.test(str),
  checkInvalidLowerCase: str => !/[a-z]/.test(str)
}

export const imageWithExtension = filename => {
  if (!filename)
    return filename
  else {
    const length = filename.length
    const lastThree = filename.substring(length-4)
    const lastFour = filename.substring(length-5)
    const hasFileExtension = lastThree.startsWith('.') || lastFour.startsWith('.')

    return hasFileExtension ? filename : filename + '.jpg'
  }
}

export const imageWithExtensionSmall = filename => {
  if (!filename)
    return filename
  else {
    let output
    if (filename.endsWith('.jpg')) {
      const length = filename.length
      output = filename.slice(0, length - 4) + '_sm.jpg'
    } else {
      output = filename + '_sm.jpg'
    }
    return output
  }
}

export const dateHasPassedTheRepublishThreshold = date => {
  const republishThreshold = 21 // FIXME: pick this value from the config
  const pubDate = new Date(date)
  const now = new Date()
  const minuteMargin = 1000 * 60
  const diffTime = Math.abs((now.valueOf() + minuteMargin) - pubDate.valueOf())
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays >= republishThreshold
}

export const isRequiredAttribute = a => a.mpv_required
                                           // || a.tags && !a.tags.hidden && (a.tags.required || a.tags.catalog_required) ||
                                           // a.hierarchy && (!a.tags || a.tags && !a.tags.hidden) && (a.hierarchy === 'PARENT_PK' || a.hierarchy === 'CHILD_PK')

export const sortMedia = (m, n) => {
  if ( m.image_file_order < n.image_file_order ) {
    return -1;
  }
  if ( m.image_file_order > n.image_file_order ) {
    return 1;
  }
  return 0;
}

const attributeRules = {
  'VEHICLE_YEAR': value => !!value && (value > 1900 && value < 2100)
}

export const checkAttributeValidity = (attr, value) => {
  const setValueToInt = parseInt(value)
  const included = Object.keys(attributeRules).includes(attr)

  return !included || included && attributeRules[attr](setValueToInt)
}

export const filterTagName = filter => {
  let filteredKey = filter.key.split('_')
  filteredKey = filteredKey[filteredKey.length - 1]
  const filteredVal = filter.val.split('_')[0]
  if (filter.key === 'cities')
    return filterLocation(filteredVal)
  else
    return `${filteredKey}: ${filteredVal}`
}

export const filterLocation = location => {
  if (location) {
    location = location.replace('Ciudad de ', '')
    location = location.replace('Ciudad De ', '')
    location = location.replace('Provincia de ', '')
    location = location.replace('Provincia De ', '')
    location = location.replace('Provincia del ', '')
    location = location.replace('Provincia Del ', '')
  }
  if (location && location.endsWith('Argentina')) {
    const stringArray = location.split(',')
    stringArray.splice(stringArray.length - 1, 1)
    return stringArray.join(',') || location
  } else {
    return location
  }
}

export const filterNumbersOnly = input => [...input]
  .filter(c => /\d/.test(c))
  .join('')

const bsas = /^(11)(\d{0,8})/
const metropolitan = /^(388|387|385|383|381|380|379|376|370|364|362|358|353|351|348|345|343|342|341|336|299|298|297|294|291|280|266|264|263|261|260|249|237|236|230|223|221|220)(\d{0,7})/
const interior = /^(3894|3892|3891|3888|3887|3886|3885|3878|3877|3876|3873|3869|3868|3867|3865|3863|3862|3861|3858|3857|3856|3855|3854|3846|3845|3844|3843|3841|3838|3837|3835|3832|3827|3826|3825|3821|3786|3782|3781|3777|3775|3774|3773|3772|3758|3757|3756|3755|3754|3751|3743|3741|3735|3734|3731|3725|3721|3718|3716|3715|3711|3585|3584|3583|3582|3576|3575|3574|3573|3572|3571|3564|3563|3562|3549|3548|3547|3546|3544|3543|3542|3541|3537|3533|3532|3525|3524|3522|3521|3498|3497|3496|3493|3492|3491|3489|3487|3483|3482|3476|3472|3471|3469|3468|3467|3466|3465|3464|3463|3462|3460|3458|3456|3455|3454|3447|3446|3445|3444|3442|3438|3437|3436|3435|3409|3408|3407|3406|3405|3404|3402|3401|3400|3388|3387|3385|3382|3329|3327|2983|2982|2972|2966|2964|2963|2962|2954|2953|2952|2948|2946|2945|2942|2940|2936|2935|2934|2933|2932|2931|2929|2928|2927|2926|2925|2924|2923|2922|2921|2920|2903|2902|2901|2658|2657|2656|2655|2652|2651|2648|2647|2646|2626|2625|2624|2622|2478|2477|2475|2474|2473|2396|2395|2394|2393|2392|2358|2357|2356|2355|2354|2353|2352|2346|2345|2344|2343|2342|2338|2337|2336|2335|2334|2333|2331|2326|2325|2324|2323|2320|2317|2316|2314|2302|2297|2296|2292|2291|2286|2285|2284|2283|2281|2274|2273|2272|2271|2268|2267|2266|2265|2264|2262|2261|2257|2255|2254|2252|2246|2245|2244|2243|2242|2241|2229|2227|2226|2225|2224|2223|2221|2202)(\d{0,6})/

export const formatPhone = num => {
  let pattern
  if (bsas.test(num)) pattern = bsas
  else if (metropolitan.test(num)) pattern = metropolitan
  else if (interior.test(num)) pattern = interior
  else return num
  return num.replace(pattern, `($1) $2`)
}

export const titleCase = input => {
  if (!input) return ''
  const string = String(input)
  return string.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.substring(1)).join(' ')
}

/**
 * Device Fingerprint Helper
 * Generates a unique fingerprint based on stable device characteristics
 */

/**
 * Generate a unique device fingerprint
 * @returns {Promise<string>} SHA-256 hash of device characteristics
 */
export const generateDeviceFingerprint = async () => {

  try {
    const getCanvasFingerprint = () => {
      try {
        if (typeof document === 'undefined') {
          return 'no-document';
        }
        const canvas = document.createElement('canvas');
        if (!canvas || !canvas.getContext) {
          return 'no-canvas-support';
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return 'no-2d-context';
        }

        ctx.textBaseline = "top";
        ctx.font = "14px 'Arial'";
        ctx.fillText("fingerprint", 2, 2);
        const result = canvas.toDataURL();
        return result;
      } catch (e) {
        return 'canvas-error';
      }
    };

    const getWebGLFingerprint = () => {
      try {
        if (typeof document === 'undefined') {
          return 'no-document';
        }
        const canvas = document.createElement('canvas');
        if (!canvas || !canvas.getContext) {
          return 'no-canvas-support';
        }

        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) {
          return 'no-webgl';
        }

        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        const extensions = gl.getSupportedExtensions();

        const result = {
          vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : (gl.getParameter(gl.VENDOR) || 'unknown'),
          renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : (gl.getParameter(gl.RENDERER) || 'unknown'),
          version: gl.getParameter(gl.VERSION) || 'unknown',
          shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || 'unknown',
          extensions: extensions ? extensions.sort().join(',') : 'none'
        };

        return result;
      } catch (e) {
        return 'webgl-error';
      }
    };

    const getAudioFingerprint = () => {
      try {
        if (typeof window === 'undefined') {
          return 'no-window';
        }
        if (!window.AudioContext && !window.webkitAudioContext) {
          return 'no-audio-context';
        }

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (!audioContext) {
          return 'audio-context-failed';
        }

        const fingerprint = {
          sampleRate: audioContext.sampleRate || 'unknown',
          maxChannelCount: audioContext.destination ? audioContext.destination.maxChannelCount : 'unknown',
          numberOfInputs: audioContext.destination ? audioContext.destination.numberOfInputs : 'unknown',
          numberOfOutputs: audioContext.destination ? audioContext.destination.numberOfOutputs : 'unknown',
          channelCount: audioContext.destination ? audioContext.destination.channelCount : 'unknown',
          channelCountMode: audioContext.destination ? audioContext.destination.channelCountMode : 'unknown',
          channelInterpretation: audioContext.destination ? audioContext.destination.channelInterpretation : 'unknown'
        };


        // Cerrar el contexto de audio para liberar recursos
        if (audioContext.close) {
          audioContext.close().catch(() => {
          });
        }

        return fingerprint;
      } catch (e) {
        return 'audio-not-available';
      }
    };

    const getFontFingerprint = () => {
      try {
        if (typeof document === 'undefined') {
          return 'no-document';
        }

        const fonts = [
          'Arial', 'Arial Black', 'Arial Narrow', 'Calibri', 'Cambria', 'Century Gothic',
          'Comic Sans MS', 'Consolas', 'Courier', 'Courier New', 'Georgia', 'Helvetica',
          'Impact', 'Lucida Console', 'Lucida Sans Unicode', 'Microsoft Sans Serif',
          'Palatino', 'Tahoma', 'Times', 'Times New Roman', 'Trebuchet MS', 'Verdana'
        ];

        const canvas = document.createElement('canvas');
        if (!canvas || !canvas.getContext) {
          return 'no-canvas-support';
        }

        const ctx = canvas.getContext('2d');
        if (!ctx || !ctx.measureText) {
          return 'no-2d-context';
        }

        const baseline = 'mpv-fingerprint-test';

        ctx.font = '14px serif';
        const baselineWidth = ctx.measureText(baseline).width;

        if (!baselineWidth || baselineWidth === 0) {
          return 'font-measurement-failed';
        }


        const availableFonts = fonts.filter(font => {
          try {
            ctx.font = `14px ${font}, serif`;
            const width = ctx.measureText(baseline).width;
            return width && width !== baselineWidth;
          } catch (e) {
            return false;
          }
        });

        return availableFonts.join(',') || 'no-fonts-detected';
      } catch (e) {
        return 'font-detection-error';
      }
    };

    const getBasicFingerprint = () => {
      const basic = {
        userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
        platform: (typeof navigator !== 'undefined' && navigator.platform) || 'unknown',
        language: (typeof navigator !== 'undefined' && navigator.language) || 'unknown',
        languages: (typeof navigator !== 'undefined' && navigator.languages)
          ? navigator.languages.join(',')
          : ((typeof navigator !== 'undefined' && navigator.language) || 'unknown'),
        timezone: (() => {
          try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
          } catch (e) {
            return 'timezone-error';
          }
        })(),
        timezoneOffset: (() => {
          try {
            return new Date().getTimezoneOffset() || 0;
          } catch (e) {
            return 0;
          }
        })()
      };
      return basic;
    };

    const getHardwareFingerprint = () => {
      const hardware = {
        deviceMemory: (typeof navigator !== 'undefined' && navigator.deviceMemory) || 'unknown',
        hardwareConcurrency: (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 'unknown',
        touchSupport: (() => {
          try {
            // Use a more robust check for touch support
            return (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
                   (typeof window !== 'undefined' && window.TouchEvent !== undefined);
          } catch (e) {
            return false;
          }
        })(),
        maxTouchPoints: (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0
      };
      return hardware;
    };

    const getScreenFingerprint = () => {
      try {
        if (typeof screen === 'undefined') {
          return 'no-screen';
        }

        const screenData = {
          width: screen.width || 0,
          height: screen.height || 0,
          colorDepth: screen.colorDepth || 0,
          pixelDepth: screen.pixelDepth || 0,
          availWidth: screen.availWidth || 0,
          availHeight: screen.availHeight || 0,
          pixelRatio: (typeof window !== 'undefined' && window.devicePixelRatio) || 1
        };
        return screenData;
      } catch (e) {
        return 'screen-error';
      }
    };

    const getBrowserFingerprint = () => {
      const browser = {
        doNotTrack: (typeof navigator !== 'undefined' && navigator.doNotTrack) || 'unknown',
        cookieEnabled: (() => {
          try {
            return (typeof navigator !== 'undefined' && navigator.cookieEnabled) || false;
          } catch (e) {
            return false;
          }
        })(),
        javaEnabled: (() => {
          try {
            return (typeof navigator !== 'undefined' &&
                   typeof navigator.javaEnabled === 'function' &&
                   navigator.javaEnabled()) || false;
          } catch (e) {
            return false;
          }
        })(),
        sessionStorage: (() => {
          try {
            return typeof sessionStorage !== 'undefined';
          } catch (e) {
            return false;
          }
        })(),
        localStorage: (() => {
          try {
            return typeof localStorage !== 'undefined';
          } catch (e) {
            return false;
          }
        })(),
        indexedDB: (() => {
          try {
            return typeof indexedDB !== 'undefined';
          } catch (e) {
            return false;
          }
        })()
      };
      return browser;
    };


    // Construir el fingerprint con fallbacks robustos
    const fingerprint = {
      // Datos básicos (siempre disponibles)
      ...getBasicFingerprint(),

      // Características del hardware (con fallbacks)
      hardware: getHardwareFingerprint(),

      // Pantalla y visualización (con fallbacks)
      screen: getScreenFingerprint(),

      // Configuraciones del navegador (con fallbacks)
      browser: getBrowserFingerprint(),

      // Fingerprints avanzados (todos con manejo de errores)
      canvas: getCanvasFingerprint(),
      webgl: getWebGLFingerprint(),
      audio: getAudioFingerprint(),
      fonts: getFontFingerprint()
    };


    const fingerprintString = JSON.stringify(fingerprint, null, 0);

    const hash = await createDeviceFingerprintHash(fingerprintString);

    return hash;
  } catch (error) {

    // Fallback ultra-básico que siempre debería funcionar
    const fallbackData = {
      timestamp: Date.now(),
      random: Math.random().toString(36).substr(2, 9),
      userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
      platform: (typeof navigator !== 'undefined' && navigator.platform) || 'unknown',
      language: (typeof navigator !== 'undefined' && navigator.language) || 'unknown'
    };


    try {
      const fallbackString = JSON.stringify(fallbackData);
      const fallbackHash = await createDeviceFingerprintHash(fallbackString);
      const result = 'fallback_' + fallbackHash.substr(0, 16);
      return result;
    } catch (hashError) {
      // Si incluso el hash falla, usar un método ultra-simple
      const basicResult = 'basic_fallback_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      return basicResult;
    }
  }
};

/**
 * Create SHA-256 hash from string data
 * @param {string} data - Data to hash
 * @returns {Promise<string>} Hexadecimal hash string
 */
const createDeviceFingerprintHash = async (data) => {
  try {
    // Verificar si Web Crypto API está disponible
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      // Fallback a un hash simple si Web Crypto no está disponible
      return createSimpleHash(data);
    }

    const encoder = new TextEncoder();
    const hashData = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', hashData);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    return createSimpleHash(data);
  }
};

/**
 * Simple hash function fallback for environments without Web Crypto API
 * @param {string} str - String to hash
 * @returns {string} Simple hash string
 */
const createSimpleHash = (str) => {
  let hash = 0;
  if (str.length === 0) {
    return hash.toString();
  }

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convertir a hexadecimal y asegurar que sea positivo
  const result = Math.abs(hash).toString(16).padStart(8, '0');
  return result;
};

/**
 * Get device characteristics for debugging
 * @returns {Object} Raw device characteristics
 */
export const getDeviceCharacteristics = () => {
  return {
    screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    languages: navigator.languages ? navigator.languages.join(',') : navigator.language,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency || 1,
    deviceMemory: navigator.deviceMemory || 'unknown',
    cookieEnabled: navigator.cookieEnabled,
    userAgent: navigator.userAgent,
    touchPoints: navigator.maxTouchPoints || 0,
    pixelRatio: window.devicePixelRatio || 1
  }
}

/**
 * Función de prueba para el fingerprint - úsala en la consola del browser
 */
export const testDeviceFingerprint = async () => {

  try {
    const fingerprint = await generateDeviceFingerprint();
    return fingerprint;
  } catch (error) {
    throw error;
  }
}



// WEBPACK FOOTER //
// ./src/helpers.js